/**
 * CanvasDepartChoix — « Sur quel papier à en-tête commencer ? » : la page blanche et chaque papier
 * du cabinet, comme l'écran d'accueil de Word.
 *
 * Composant de présentation pur : il ne lit rien et ne charge rien, il rend le choix à
 * l'appelant. Le branchement (liste des en-têtes, remplacement du document, clôture) vit dans
 * `CanvasDepartChoixVoile`.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Un papier proposable : un en-tête du cabinet qui porte un document Word. */
export interface PapierDeDepart {
  id: string;
  name: string;
  usageNote: string | null;
  isDefault: boolean;
  /** Teinte du bandeau de la vignette. Sans rendu Word, la vignette est un schéma, pas un aperçu. */
  teinte?: string;
}

interface CanvasDepartChoixProps {
  papiers: readonly PapierDeDepart[];
  /** `null` = la page blanche (gabarit Hector, sans en-tête ni pied de page). */
  onChoisir: (papierId: string | null) => void;
  /** Un choix est en cours d'application : les cartes ne répondent plus, pour ne pas remplacer deux fois. */
  occupe?: boolean;
}

/** Vignette A4 schématique : un bandeau d'en-tête, des lignes de texte, un filet de pied. */
function Vignette({ teinte }: { teinte: string | null }): React.JSX.Element {
  return (
    <div className="flex w-full flex-col rounded-sm border border-grey-200 bg-white p-2 shadow-sm" style={{ aspectRatio: '210 / 297' }}>
      {teinte ? (
        <div className="h-3 w-2/5 rounded-[2px]" style={{ backgroundColor: teinte }} />
      ) : (
        <div className="h-3" />
      )}
      <div className="mt-3 flex-1 space-y-1.5">
        {[100, 92, 96, 70, 0, 100, 88, 94, 60].map((largeur, i) => (
          <div
            // Lignes décoratives d'une liste fixe : le rang est leur seule identité.
            key={i}
            className={cn('h-[3px] rounded-full', largeur === 0 ? 'bg-transparent' : 'bg-grey-200')}
            style={{ width: `${largeur}%` }}
          />
        ))}
      </div>
      {teinte && <div className="mt-2 h-px w-full" style={{ backgroundColor: teinte }} />}
      {teinte && <div className="mx-auto mt-1 h-[3px] w-3/5 rounded-full bg-grey-200" />}
    </div>
  );
}

export function CanvasDepartChoix({ papiers, onChoisir, occupe = false }: CanvasDepartChoixProps): React.JSX.Element {
  const parDefaut = papiers.find((p) => p.isDefault) ?? null;
  const cartes: Array<{ id: string | null; titre: string; note: string | null; teinte: string | null; defaut: boolean }> = [
    ...papiers.map((p) => ({
      id: p.id,
      titre: p.name,
      note: p.usageNote,
      teinte: p.teinte ?? '#1a337f',
      defaut: p.isDefault,
    })),
    { id: null, titre: 'Page blanche', note: 'Sans en-tête ni pied de page', teinte: null, defaut: false },
  ];

  return (
    // `min-h-full`, pas `h-full` : avec beaucoup de modèles la liste dépasse le canvas, et un
    // contenu centré dans une hauteur fixe perdrait son titre au-dessus de la zone qui défile.
    <div className="flex min-h-full w-full flex-col items-center justify-center bg-stone-50 px-6 py-10">
      <h2 className="text-base font-medium text-charcoal">Sur quel papier à en-tête commencer ?</h2>
      <p className="mt-1 max-w-md text-center text-xs text-grey-500">
        {parDefaut
          ? `Sans choix de votre part, le document reste sur « ${parDefaut.name} ». Hector, lui, choisit selon ce qu'il rédige.`
          : "Sans choix de votre part, le document reste sur une page blanche. Hector, lui, choisit selon ce qu'il rédige."}
      </p>

      <ul className="mt-6 flex flex-wrap justify-center gap-4">
        {cartes.map((carte) => (
          <li key={carte.id ?? 'aucun'} className="w-36">
            <button
              type="button"
              onClick={() => onChoisir(carte.id)}
              disabled={occupe}
              className="group w-full rounded-md p-2 text-left transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-progress disabled:opacity-60"
            >
              <div className="transition-transform group-hover:-translate-y-0.5">
                <Vignette teinte={carte.teinte} />
              </div>
              <div className="mt-2 truncate text-[13px] font-medium text-charcoal" title={carte.titre}>
                {carte.titre}
              </div>
              {carte.defaut && (
                <span className="mt-0.5 inline-block rounded-full bg-grey-100 px-1.5 py-px text-[10px] text-grey-600">
                  Par défaut
                </span>
              )}
              {carte.note && (
                <p className="mt-0.5 line-clamp-2 text-[11px] leading-tight text-grey-500">{carte.note}</p>
              )}
            </button>
          </li>
        ))}
        <li className="w-36">
          <Link
            to="/personnalisation#charte/en-tete"
            className="group block w-full rounded-md p-2 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <div style={{ aspectRatio: '210 / 297' }} className="flex w-full items-center justify-center rounded-sm border border-dashed border-grey-300 text-grey-400 transition-colors group-hover:border-grey-500 group-hover:text-grey-600">
              <Plus className="h-6 w-6" aria-hidden />
            </div>
            <div className="mt-2 text-[13px] font-medium text-charcoal">Ajouter un papier à en-tête</div>
            <p className="mt-0.5 text-[11px] leading-tight text-grey-500">Déposez un de vos documents Word</p>
          </Link>
        </li>
      </ul>
    </div>
  );
}
